// eslint-disable-next-line @typescript-eslint/no-unused-vars
import React, { createContext, useContext, useState, useEffect } from "react";
import logger from "../../logger";
import {
  loginWithPopup,
  logout,
  isAuthenticated,
  getUser,
  restoreTokens,
  clearStoredTokens,
  loadTokensFromStorage,
  persistUserInfo,
  restoreUserInfo,
} from "../config/popupAuth";
import oktaAuth from "../config/oktaAuth";
import { fetchAndStoreOrganizations } from "../services/organizationService";

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
  const [authState, setAuthState] = useState({
    isAuthenticated: false,
    user: null,
    loading: true,
    initialized: false,
    persistenceError: null,
  });

  useEffect(() => {
    checkAuth();
  }, []);

  const checkAuth = async () => {
    try {
      logger.debug("Checking authentication state");

      const urlParams = new URLSearchParams(window.location.search);
      const hasLogoutParam = urlParams.has("logout");

      if (hasLogoutParam) {
        logger.debug("Detected logout redirect");
        await clearStoredTokens();

        try {
          const cleanUrl = window.location.pathname;
          if (window.location.replace) {
            window.location.replace(cleanUrl);
          } else if (window.location.href) {
            window.location.href = cleanUrl;
          }
        } catch {
          logger.warn("Could not clean URL");
        }

        setAuthState({
          isAuthenticated: false,
          user: null,
          loading: false,
          initialized: true,
          persistenceError: null,
        });
        return;
      }

      const cachedUser = await restoreUserInfo();
      if (cachedUser) {
        logger.debug("User info found in cache");
        setAuthState({
          isAuthenticated: true,
          user: cachedUser,
          loading: true,
          initialized: false,
          persistenceError: null,
        });
      }

      const restoredTokens = await restoreTokens();

      if (restoredTokens) {
        logger.debug("Tokens found, loading into auth SDK");
        const loaded = await loadTokensFromStorage();
        if (!loaded) {
          logger.warn("Tokens found but failed to load into tokenManager");
        }
      }

      await new Promise((r) => setTimeout(r, 300));

      const authenticated = await isAuthenticated();
      logger.debug("Authentication check completed");
      if (authenticated || restoredTokens) {
        logger.debug("Fetching user information");
        const user = await getUser();

        if (user) {
          logger.debug("User authenticated successfully");
          let persistProfileError = null;
          try {
            await persistUserInfo(user);
          } catch (persistErr) {
            logger.error("Failed to persist user profile: %s", persistErr?.message);
            persistProfileError =
              "Could not save your profile to device storage. Excel formulas may not work after you close the add-in. Try signing out and signing in again.";
          }
          setAuthState({
            isAuthenticated: true,
            user: user,
            loading: false,
            initialized: true,
            persistenceError: persistProfileError,
          });

          try {
            const { getStoredOrganizations } = await import("../services/organizationService");
            const storedOrgs = await getStoredOrganizations();
            if (!storedOrgs || storedOrgs.length === 0) {
              await fetchAndStoreOrganizations();
              logger.debug("Organizations fetched on auth check");
            }
          } catch (orgError) {
            logger.error("Error fetching organizations on auth check: %s", orgError?.message);
          }

          return;
        } else {
          logger.warn("Authentication check passed but getUser() returned null");

          if (cachedUser) {
            logger.info("Using cached user info as fallback");
            setAuthState({
              isAuthenticated: true,
              user: cachedUser,
              loading: false,
              initialized: true,
              persistenceError: null,
            });
            return;
          }

          try {
            await new Promise((r) => setTimeout(r, 200));
            const idToken = oktaAuth.tokenManager.get("idToken");
            if (idToken && idToken.claims) {
              logger.debug("Extracting user from token claims as fallback");
              const fallbackUser = {
                sub: idToken.claims.sub,
                email: idToken.claims.email,
                name:
                  idToken.claims.name ||
                  idToken.claims.preferred_username ||
                  idToken.claims.email?.split("@")[0] ||
                  "User",
                ...idToken.claims,
              };
              let persistFallbackError = null;
              try {
                await persistUserInfo(fallbackUser);
              } catch (persistErr) {
                logger.error("Failed to persist fallback user: %s", persistErr?.message);
                persistFallbackError =
                  "Could not save your profile to device storage. Excel formulas may not work after you close the add-in. Try signing out and signing in again.";
              }
              setAuthState({
                isAuthenticated: true,
                user: fallbackUser,
                loading: false,
                initialized: true,
                persistenceError: persistFallbackError,
              });
              return;
            }
          } catch (idTokenError) {
            logger.error("Error extracting user from token: %s", idTokenError?.message);
          }

          if (restoredTokens || authenticated) {
            logger.debug("Treating as authenticated based on token presence");
            setAuthState({
              isAuthenticated: true,
              user: cachedUser || null,
              loading: false,
              initialized: true,
              persistenceError: null,
            });
            return;
          }
        }
      }

      if (!restoredTokens && !authenticated) {
        logger.warn("User not authenticated and no tokens found");
        setAuthState({
          isAuthenticated: false,
          user: null,
          loading: false,
          initialized: true,
          persistenceError: null,
        });
      } else if (restoredTokens || authenticated) {
        logger.debug("Keeping user logged in");
        setAuthState({
          isAuthenticated: true,
          user: cachedUser || null,
          loading: false,
          initialized: true,
          persistenceError: null,
        });

        try {
          const { getStoredOrganizations } = await import("../services/organizationService");
          const storedOrgs = await getStoredOrganizations();
          if (!storedOrgs || storedOrgs.length === 0) {
            await fetchAndStoreOrganizations();
            logger.debug("Organizations fetched on refresh");
          }
        } catch (orgError) {
          logger.error("Error fetching organizations on refresh: %s", orgError?.message);
        }
      } else {
        setAuthState({
          isAuthenticated: false,
          user: null,
          loading: false,
          initialized: true,
          persistenceError: null,
        });
      }
    } catch (err) {
      logger.error("Error in checkAuth: %s", err?.message);
      setAuthState({
        isAuthenticated: false,
        user: null,
        loading: false,
        initialized: true,
        persistenceError: null,
      });
    }
  };

  const handleLogin = async (environment) => {
    try {
      setAuthState((prev) => ({
        ...prev,
        loading: true,
        persistenceError: null,
      }));
      logger.debug("Starting login process");

      if (!environment) {
        throw new Error("Environment must be specified");
      }

      const user = await loginWithPopup(environment);
      logger.debug("Login successful");

      let profilePersistError = null;
      if (user) {
        try {
          await persistUserInfo(user);
        } catch (persistErr) {
          logger.error("Failed to persist user after login: %s", persistErr?.message);
          profilePersistError =
            "Signed in, but your profile could not be saved to device storage. Some features may be limited until you sign out and sign in again.";
        }
      }

      await new Promise((resolve) => setTimeout(resolve, 100));

      const authenticated = await isAuthenticated();
      logger.debug("Authentication verified");

      if (authenticated && user) {
        setAuthState({
          isAuthenticated: true,
          user: user,
          loading: false,
          initialized: true,
          persistenceError: profilePersistError,
        });
        logger.debug("Auth state updated");

        try {
          await fetchAndStoreOrganizations();
          logger.debug("Organizations fetched after login");
        } catch (orgError) {
          logger.error("Error fetching organizations after login: %s", orgError?.message);
        }

        return user;
      } else if (user) {
        logger.warn("Auth check returned false but user exists, setting auth state anyway");
        setAuthState({
          isAuthenticated: true,
          user: user,
          loading: false,
          initialized: true,
          persistenceError: profilePersistError,
        });

        try {
          await fetchAndStoreOrganizations();
          logger.debug("Organizations fetched after login");
        } catch (orgError) {
          logger.error("Error fetching organizations after login: %s", orgError?.message);
        }

        return user;
      } else {
        throw new Error("Authentication verification failed after login: No user received");
      }
    } catch (error) {
      logger.error("Login error: %s", error?.message);
      setAuthState((prev) => ({
        ...prev,
        loading: false,
        initialized: true,
        persistenceError: null,
      }));
      throw error;
    }
  };

  const handleLogout = async () => {
    try {
      setAuthState((prev) => ({ ...prev, loading: true }));
      logger.info("Initiating logout");

      await logout();

      setAuthState({
        isAuthenticated: false,
        user: null,
        loading: false,
        initialized: true,
        persistenceError: null,
      });

      const cacheBuster = Date.now();
      logger.debug("Redirecting to login page");
      window.location.href = `taskpane.html?logout=${cacheBuster}`;
    } catch (error) {
      logger.error("Logout error: %s", error?.message);
      setAuthState({
        isAuthenticated: false,
        user: null,
        loading: false,
        initialized: true,
        persistenceError: null,
      });

      const cacheBuster = Date.now();
      window.location.href = `taskpane.html?logout=${cacheBuster}`;
    }
  };

  const clearPersistenceError = () => {
    setAuthState((prev) => ({ ...prev, persistenceError: null }));
  };

  const value = {
    ...authState,
    login: handleLogin,
    logout: handleLogout,
    checkAuth: checkAuth,
    clearPersistenceError,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};

export default AuthContext;
